import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export function AiSettings({ onApply }) {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);

  const ask = async () => {
    if (!instruction.trim()) { toast.error("Tell the assistant what to change"); return; }
    setLoading(true); setProposal(null);
    try {
      const r = await api.post("/admin/ai-settings", { instruction });
      setProposal(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI request failed"); }
    setLoading(false);
  };

  const apply = async () => {
    const changes = proposal?.changes || {};
    if (!Object.keys(changes).length) { toast.error("No changes to apply"); return; }
    await onApply(changes);
    setProposal(null); setInstruction("");
  };

  const changes = proposal?.changes || {};
  return (
    <div className="border border-[#C79A3B] bg-[#FBF3EC] p-4 mb-8" data-testid="ai-settings">
      <div className="flex items-center gap-2 font-bold mb-2"><Sparkles size={18} className="text-[#C79A3B]" /> AI settings assistant</div>
      <p className="text-xs text-[#6B7280] mb-3">Describe the change in plain English. The assistant proposes the exact field changes for you to review before saving. Example: "Set the summer offer to 12% and extend it to 90 days".</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="ai-settings-input" value={instruction} onChange={e => setInstruction(e.target.value)} onKeyDown={e => e.key === "Enter" && ask()} placeholder="e.g. raise the base price to £9,000 and area rate to £2,200/sqm" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
        <button data-testid="ai-settings-ask" onClick={ask} disabled={loading} className="de-btn-primary">{loading ? "Thinking…" : "Ask AI"}</button>
      </div>

      {proposal && (
        <div className="mt-4 border border-[#E5E7EB] bg-white p-4" data-testid="ai-settings-proposal">
          {proposal.summary && <p className="text-sm font-bold mb-2">{proposal.summary}</p>}
          {Object.keys(changes).length > 0 ? (
            <table className="w-full text-sm mb-3">
              <thead className="text-left text-xs uppercase tracking-wider text-[#6B7280]"><tr><th className="py-1">Field</th><th className="py-1">New value</th></tr></thead>
              <tbody>
                {Object.entries(changes).map(([k, v]) => (
                  <tr key={k} className="border-t border-[#E5E7EB]"><td className="py-1 font-mono text-xs">{k}</td><td className="py-1 font-bold">{String(v)}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-[#6B7280] mb-3">No field changes proposed.</p>}
          {(proposal.warnings || []).length > 0 && (
            <ul className="text-xs text-[#C79A3B] mb-3 list-disc pl-4">{proposal.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          )}
          {Object.keys(changes).length > 0 && (
            <button data-testid="ai-settings-apply" onClick={apply} className="de-btn-primary">Apply &amp; save</button>
          )}
        </div>
      )}
    </div>
  );
}
